// Sport packs: activate (seeds the pack's defaults), archive (soft-disable,
// confirmed), restore. The server refuses archiving the last enabled sport
// (409 last_sport) and the league's default sport while another is enabled
// (409 default_sport); the UI says so before you try, and shows the server's
// message if it refuses anyway.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { SportBootstrapResult, TenantSport } from '@/api/types'
import { Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, InlineAlert, SkeletonRows } from '@/components/ui'
import { cn, formatDate } from '@/lib/format'
import { useQuery } from '@/lib/query'
import { useTenant } from '../tenantContext'

type RowMessage = { tone: 'good' | 'bad'; text: string }

function bootstrapMessage(name: string, r: SportBootstrapResult): string {
  const ran = r.ran_steps.length
  const skipped = r.skipped_steps.length
  if (ran === 0) return `${name} is enabled again. All ${skipped} setup steps were already in place.`
  return `${name} is enabled. Ran ${ran} setup step${ran === 1 ? '' : 's'}${skipped ? `, skipped ${skipped} already done` : ''}.`
}

function SportState({ s, isDefault }: { s: TenantSport; isDefault: boolean }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {s.is_enabled ? <Badge tone="good">Enabled</Badge> : s.archived_at ? <Badge tone="neutral">Archived</Badge> : <Badge tone="outline">Not activated</Badge>}
      {isDefault ? <Badge tone="accent">Default</Badge> : null}
      {!s.is_registered ? (
        <Badge tone="bad" title="This backend doesn't have this sport pack registered">
          Unregistered
        </Badge>
      ) : null}
    </span>
  )
}

export default function SportsTab() {
  const { tenant, reload } = useTenant()
  const key = ['tenant', tenant.id, 'sports'] as const
  const { data, error, status, refetch } = useQuery(key, (signal) => api.tenants.sports.list(tenant.id, signal))
  const [busy, setBusy] = useState<string | null>(null)
  const [messages, setMessages] = useState<Record<string, RowMessage>>({})
  const [archiving, setArchiving] = useState<TenantSport | null>(null)

  const enabledCount = (data ?? []).filter((s) => s.is_enabled).length

  // reload() refetches every ['tenant', …] query: the detail (sports, default
  // sport) and this list.
  const after = reload

  const run = async (s: TenantSport, fn: () => Promise<string>) => {
    setBusy(s.sport_id)
    setMessages((m) => {
      const next = { ...m }
      delete next[s.sport_id]
      return next
    })
    try {
      const text = await fn()
      setMessages((m) => ({ ...m, [s.sport_id]: { tone: 'good', text } }))
      after()
    } catch (err) {
      setMessages((m) => ({ ...m, [s.sport_id]: { tone: 'bad', text: errorMessage(err) } }))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      <Card flush className="overflow-hidden">
        <div className="px-4 pt-4 sm:px-5">
          <CardHeader
            title="Sport packs"
            description="Activating a pack seeds its homepage layout, theme, sponsor tiers, score-bug template and Fan AI defaults. Re-activating only fills in what's missing. A league always needs at least one enabled sport."
          />
        </div>
        {status === 'loading' ? (
          <SkeletonRows rows={2} label="Loading sport packs" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load sport packs" onRetry={refetch} />
        ) : !data?.length ? (
          <p className="border-t border-line px-4 py-6 text-[13px] text-muted sm:px-5">No sport packs are registered on this backend.</p>
        ) : (
          <ul className="divide-y divide-line border-t border-line" aria-label="Sport packs">
            {data.map((s) => {
              const isDefault = s.sport_id === tenant.default_sport_id
              const onlyEnabled = s.is_enabled && enabledCount <= 1
              // The default can't be archived until another sport is the default.
              const defaultLocked = s.is_enabled && isDefault && !onlyEnabled
              const msg = messages[s.sport_id]
              return (
                <li key={s.sport_id} className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-5">
                  <div className="grid min-w-0 gap-1.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                      <span className="font-semibold text-ink">{s.display_name}</span>
                      <span className="font-mono text-xs text-subtle">{s.sport_id}</span>
                      <SportState s={s} isDefault={isDefault} />
                    </div>
                    <p className="text-xs text-muted">
                      {s.is_enabled && s.activated_at
                        ? `Activated ${formatDate(s.activated_at)}${s.activated_by_name || s.activated_by_email ? ` by ${s.activated_by_name || s.activated_by_email}` : ''}.`
                        : s.archived_at
                          ? `Archived ${formatDate(s.archived_at)}. League admins can't reach its pages until it's restored.`
                          : 'Not activated yet. Activating seeds its defaults.'}
                      {onlyEnabled ? " It's the only enabled sport, so it can't be archived." : null}
                      {defaultLocked ? (
                        <>
                          {' '}
                          This is the default sport. Change the default in{' '}
                          <Link to="../settings" className="font-semibold text-accent hover:underline">
                            Settings
                          </Link>{' '}
                          first, then archive it.
                        </>
                      ) : null}
                    </p>
                    {msg ? (
                      <p role={msg.tone === 'bad' ? 'alert' : 'status'} className={cn('text-xs', msg.tone === 'good' ? 'text-good' : 'text-bad')}>
                        {msg.text}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.is_enabled ? (
                      <Button
                        size="sm"
                        onClick={() => setArchiving(s)}
                        disabled={!s.is_registered || onlyEnabled || defaultLocked || busy !== null}
                        loading={busy === s.sport_id}
                        title={onlyEnabled ? 'Activate another sport first' : defaultLocked ? 'Change the default sport in Settings first' : undefined}
                      >
                        Archive…
                      </Button>
                    ) : s.archived_at ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={!s.is_registered || busy !== null}
                        loading={busy === s.sport_id}
                        onClick={() =>
                          run(s, async () => {
                            await api.tenants.sports.restore(tenant.id, s.sport_id)
                            return `${s.display_name} is restored.`
                          })
                        }
                      >
                        Restore
                      </Button>
                    ) : s.is_registered ? (
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={busy !== null}
                        loading={busy === s.sport_id}
                        onClick={() => run(s, async () => bootstrapMessage(s.display_name, await api.tenants.sports.activate(tenant.id, s.sport_id)))}
                      >
                        Activate
                      </Button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>
      {status === 'error' && data ? <InlineAlert tone="bad">Couldn't refresh the list. Showing the last result.</InlineAlert> : null}

      <ConfirmDialog
        open={!!archiving}
        onClose={() => setArchiving(null)}
        title={archiving ? `Archive ${archiving.display_name}?` : 'Archive'}
        description={
          archiving
            ? `League admins lose access to ${archiving.display_name.toLowerCase()} pages, and fans stop seeing them, until you restore it. Nothing is deleted.`
            : undefined
        }
        confirmLabel="Archive"
        onConfirm={async () => {
          const s = archiving!
          setBusy(s.sport_id)
          try {
            await api.tenants.sports.archive(tenant.id, s.sport_id)
            setMessages((m) => ({ ...m, [s.sport_id]: { tone: 'good', text: `${s.display_name} is archived.` } }))
            after()
          } finally {
            setBusy(null)
          }
        }}
      />
    </div>
  )
}
